import React, { useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Icon, TabPane, Tabs, Text, theme, Title } from 'components';
import { ContextualChatContext, RightSidePanelPageType } from 'contexts';
import { Loading } from '@app/react-common';
import { useMediaAttachments } from 'hooks';
import { PhotosTab, DocumentsTab, LinksTab } from './mediaLinkDocTabs';

export enum ETabMenu {
  PHOTOS = 'Photos',
  DOCS = 'Documents',
  LINKS = 'Links',
}

const SideMediaLinkDocDetailsPanel = () => {
  const [selectedTabKey, setSelectedTabKey] = useState<ETabMenu>(
    ETabMenu.PHOTOS,
  );

  const { setRightSidePanelPage, channel } = useContext(ContextualChatContext);

  const { loading, links, photos, documents } = useMediaAttachments(channel);

  const tabPaneList = useMemo(
    () => [
      {
        key: ETabMenu.PHOTOS,
        label: ETabMenu.PHOTOS,
        content: <PhotosTab photos={photos} loading={loading} />,
      },
      {
        key: ETabMenu.DOCS,
        label: ETabMenu.DOCS,
        content: <DocumentsTab documentsDict={documents} loading={loading} />,
      },
      {
        key: ETabMenu.LINKS,
        label: ETabMenu.LINKS,
        content: <LinksTab linksDict={links} loading={loading} />,
      },
    ],
    [documents, links, loading, photos],
  );

  return (
    <StyledDetailsContainer>
      <StyledPrimaryHeader>
        <Icon
          type="antdArrowLeftOutlined"
          width={18}
          color="primary"
          onClick={() => {
            setRightSidePanelPage(RightSidePanelPageType.ChatDetails);
          }}
        />
        <Title level={3} style={{ margin: '25px 0px' }}>
          Media, links, & docs
        </Title>
      </StyledPrimaryHeader>
      <StyledTabsWrapper
        tabBarStyle={{ margin: 0 }}
        destroyInactiveTabPane
        activeKey={selectedTabKey}
        onTabClick={key => setSelectedTabKey(key as ETabMenu)}
      >
        {tabPaneList.map(({ key, label, content }) => (
          <StyledTabPane
            style={{ height: '100%' }}
            id="testing"
            key={key}
            tab={
              <StyledPaneTitle
                $active={key === selectedTabKey}
                data-cy={`answering-tab-${key}`}
              >
                {label}
              </StyledPaneTitle>
            }
          >
            <StyledScrollContainer>
              {loading.initial ? (
                <Loading />
              ) : (
                <ContentContainer>{content}</ContentContainer>
              )}
            </StyledScrollContainer>
          </StyledTabPane>
        ))}
      </StyledTabsWrapper>
    </StyledDetailsContainer>
  );
};

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledTabPane = styled(TabPane)`
  height: 100%;
`;

const StyledTabsWrapper = styled(Tabs)`
  height: calc(100% - 85px);

  .ant-tabs-content-holder,
  .ant-tabs-content {
    height: 100%;
  }
`;

const StyledScrollContainer = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  height: 100%;
`;

const StyledPaneTitle = styled(Text)<{ $active: boolean }>`
  color: ${({ $active }) =>
    $active ? theme.getColor('primary') : theme.getColor('textSecondary')};
`;

const StyledDetailsContainer = styled.div`
  flex: 1;
  min-width: 300px;
  height: 100%;
  background-color: #f5f5f5;
  display: flex;
  flex-direction: column;
  padding: 0px 16px;
  border-left: 1px solid #e3e3e3;
  animation: slideInRight 0.4s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  @keyframes slideInRight {
    from {
      transform: translateX(-5%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const StyledPrimaryHeader = styled.div`
  display: flex;
  justify-content: left;
  align-items: center;
  gap: 12px;
`;

export default SideMediaLinkDocDetailsPanel;
